// Admin page for managing the reference code whitelist (list, add, delete)
import { useEffect, useReducer, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from '@startus/ui';
import { referenceCodesService } from '../services/referenceCodes.service';
import { getErrorMessage, captureException } from '@startus/lib';
import type { ReferenceCode } from '../types/referenceCode';

// ── State ────────────────────────────────────────────────────────────────────

type ToastState = { message: string; type: 'success' | 'error' } | null;

type State = {
  codes: ReferenceCode[];
  loading: boolean;
  generating: boolean;
  deleteModal: { open: boolean; code: string | null };
  deleting: boolean;
  toast: ToastState;
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: ReferenceCode[] }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'GENERATE_START' }
  | { type: 'GENERATE_SUCCESS'; payload: ReferenceCode }
  | { type: 'GENERATE_ERROR'; payload: string }
  | { type: 'OPEN_DELETE_MODAL'; payload: string }
  | { type: 'CLOSE_DELETE_MODAL' }
  | { type: 'DELETE_START' }
  | { type: 'DELETE_SUCCESS'; payload: string }
  | { type: 'DELETE_ERROR'; payload: string }
  | { type: 'SET_TOAST'; payload: ToastState };

const initialState: State = {
  codes: [],
  loading: true,
  generating: false,
  deleteModal: { open: false, code: null },
  deleting: false,
  toast: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, codes: action.payload };
    case 'FETCH_ERROR':
      return { ...state, loading: false, toast: { message: action.payload, type: 'error' } };
    case 'GENERATE_START':
      return { ...state, generating: true };
    case 'GENERATE_SUCCESS':
      // Prepend so the newest code appears at the top
      return { ...state, generating: false, codes: [action.payload, ...state.codes] };
    case 'GENERATE_ERROR':
      return { ...state, generating: false, toast: { message: action.payload, type: 'error' } };
    case 'OPEN_DELETE_MODAL':
      return { ...state, deleteModal: { open: true, code: action.payload } };
    case 'CLOSE_DELETE_MODAL':
      return { ...state, deleteModal: { open: false, code: null } };
    case 'DELETE_START':
      return { ...state, deleting: true };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        deleting: false,
        deleteModal: { open: false, code: null },
        codes: state.codes.filter((c) => c.code !== action.payload),
      };
    case 'DELETE_ERROR':
      return { ...state, deleting: false, toast: { message: action.payload, type: 'error' } };
    case 'SET_TOAST':
      return { ...state, toast: action.payload };
    default:
      return state;
  }
};

// ── Component ────────────────────────────────────────────────────────────────

export default function ReferenceCodesPage() {
  const { t } = useTranslation('admin');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { codes, loading, generating, deleteModal, deleting, toast } = state;
  const [codeInput, setCodeInput] = useState('');
  const [inputError, setInputError] = useState('');

  const fetchCodes = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const data = await referenceCodesService.getReferenceCodes();
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (error) {
      captureException(error);
      dispatch({ type: 'FETCH_ERROR', payload: getErrorMessage(error, t) });
    }
  }, [t]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleGenerate = async () => {
    const trimmed = codeInput.trim();
    if (!trimmed) {
      setInputError(t('referenceCodes.validation.required'));
      return;
    }
    setInputError('');
    dispatch({ type: 'GENERATE_START' });
    try {
      const newCode = await referenceCodesService.createReferenceCode(trimmed);
      dispatch({ type: 'GENERATE_SUCCESS', payload: newCode });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('referenceCodes.success.generated'), type: 'success' },
      });
      setCodeInput('');
    } catch (error) {
      captureException(error);
      dispatch({ type: 'GENERATE_ERROR', payload: getErrorMessage(error, t) });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.code) return;
    const code = deleteModal.code;
    dispatch({ type: 'DELETE_START' });
    try {
      await referenceCodesService.deleteReferenceCode(code);
      dispatch({ type: 'DELETE_SUCCESS', payload: code });
      dispatch({
        type: 'SET_TOAST',
        payload: { message: t('referenceCodes.success.deleted'), type: 'success' },
      });
    } catch (error: unknown) {
      // Treat 404 the same as success — code is already gone
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status: number }).status
          : 0;
      if (status === 404) {
        dispatch({ type: 'DELETE_SUCCESS', payload: code });
      } else {
        captureException(error);
        dispatch({ type: 'DELETE_ERROR', payload: getErrorMessage(error, t) });
      }
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('referenceCodes.title')}</h1>
          <p className="text-sm text-base-content/60 mt-1">{t('referenceCodes.subtitle')}</p>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <input
              type="text"
              className={`input input-bordered input-sm w-48 ${inputError ? 'input-error' : ''}`}
              placeholder={t('referenceCodes.codePlaceholder')}
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value);
                if (inputError) setInputError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
              disabled={generating}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  {t('referenceCodes.generating')}
                </>
              ) : (
                t('referenceCodes.generate')
              )}
            </button>
          </div>
          {inputError && <span className="text-error text-xs">{inputError}</span>}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-base-300">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : codes.length === 0 ? (
          <div className="text-center py-16 text-base-content/50">{t('referenceCodes.empty')}</div>
        ) : (
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>{t('referenceCodes.table.code')}</th>
                <th>{t('referenceCodes.table.createdAt')}</th>
                <th className="text-right">{t('referenceCodes.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((rc) => (
                <tr key={rc.id}>
                  <td>
                    <code className="font-mono text-sm bg-base-200 px-2 py-0.5 rounded">
                      {rc.code}
                    </code>
                  </td>
                  <td className="text-sm text-base-content/70">{formatDate(rc.createdAt)}</td>
                  <td className="text-right">
                    <button
                      className="btn btn-ghost btn-sm text-error"
                      onClick={() => dispatch({ type: 'OPEN_DELETE_MODAL', payload: rc.code })}
                    >
                      {t('referenceCodes.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteModal.open && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">{t('referenceCodes.deleteTitle')}</h3>
            <p>{t('referenceCodes.deleteConfirm', { code: deleteModal.code })}</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => dispatch({ type: 'CLOSE_DELETE_MODAL' })}
                disabled={deleting}
              >
                {t('referenceCodes.cancel')}
              </button>
              <button
                className="btn btn-error ml-2"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? <span className="loading loading-spinner loading-sm" /> : null}
                {t('referenceCodes.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => dispatch({ type: 'SET_TOAST', payload: null })}
        />
      )}
    </div>
  );
}
